// src/pages/markets/board/state/boardShareLink.ts
//
// The URL is no longer where filter state lives — Firestore is. It only carries
// a share link, written on demand and read once on open.
//
// One prefixed parameter per column and field (ins.sort, ins.cap, …). Lists are
// comma-separated, which is why they are not packed into a single dot-separated
// value: an unescaped comma would silently lose a filter.
//
// The field list is a table. Adding a filter is one entry here, not an edit in
// a reader, a writer and a label map.

import {
    GATED_FIELDS,
    FILTER_WIRE_KEYS,
    type BoardFilters,
    type ColumnId,
} from '../api/boardTypes';

export const COLUMN_PARAM_PREFIX: Record<ColumnId, string> = {
    insider: 'ins',
    all_traders: 'all',
    new: 'new',
    active: 'act',
};

// Mirrors MAX_MAX_AGE_DAYS in api_screener_board.py.
export const MAX_AGE_DAYS_MAX = 90;

type FilterField = keyof BoardFilters;

type CodecKind = 'text' | 'nullableText' | 'list' | 'number' | 'enum';

interface FieldCodec {
    field: FilterField;
    param: string;
    kind: CodecKind;
    values?: readonly string[];
    label: string;
    // Gated fields are absent on the columns that do not declare them, so
    // writing them unconditionally would put empty parameters in every link.
    optional?: boolean;
}

const BASE_CODECS: FieldCodec[] = [
    { field: 'sortBy', param: 'sort', kind: 'text', label: 'Sort' },
    { field: 'sortDir', param: 'dir', kind: 'enum', values: ['asc', 'desc'], label: 'Direction' },
    { field: 'group', param: 'group', kind: 'nullableText', label: 'Group' },
    { field: 'eventsInclude', param: 'ein', kind: 'list', label: 'Categories kept' },
    { field: 'eventsExclude', param: 'eex', kind: 'list', label: 'Categories dropped' },
    { field: 'tagsInclude', param: 'tin', kind: 'list', label: 'Tags kept' },
    { field: 'tagsExclude', param: 'tex', kind: 'list', label: 'Tags dropped' },
    { field: 'tagMatch', param: 'tmatch', kind: 'enum', values: ['any', 'all'], label: 'Tag match' },
];

const GATED_CODECS: FieldCodec[] = [
    { field: 'minCapital', param: 'cap', kind: 'number', label: 'Min capital', optional: true },
    { field: 'minTraders', param: 'trd', kind: 'number', label: 'Min traders', optional: true },
    { field: 'maxAgeDays', param: 'age', kind: 'number', label: 'Max score age', optional: true },
    { field: 'scope', param: 'scope', kind: 'text', label: 'Scope', optional: true },
    { field: 'eventAgg', param: 'eagg', kind: 'text', label: 'Event aggregation', optional: true },
    { field: 'metricMode', param: 'mode', kind: 'text', label: 'Unit', optional: true },
    { field: 'driftWindow', param: 'dw', kind: 'text', label: 'Drift window', optional: true },
    { field: 'minDrift', param: 'dmin', kind: 'number', label: 'Min drift', optional: true },
    { field: 'jumpWindow', param: 'jw', kind: 'text', label: 'Jump window', optional: true },
    { field: 'minJump', param: 'jmin', kind: 'number', label: 'Min jump', optional: true },
    { field: 'minVolumeUsd', param: 'vmin', kind: 'number', label: 'Min volume', optional: true },
    // 'dir' is taken by sort direction, so this one cannot be shortened to it.
    { field: 'direction', param: 'dirn', kind: 'text', label: 'Direction of move', optional: true },
    { field: 'resolvedMode', param: 'res', kind: 'text', label: 'Resolved', optional: true },
];

export const FIELD_CODECS: FieldCodec[] = [...BASE_CODECS, ...GATED_CODECS];

// Every gated field has a codec, or a share link would drop it silently.
const CODEC_BY_FIELD = new Map(FIELD_CODECS.map(c => [c.field, c]));
for (const field of GATED_FIELDS) {
    if (!CODEC_BY_FIELD.has(field)) {
        throw new Error(`boardShareLink: no codec for gated field '${field}'`);
    }
}

// Show-all, not "back to the seed". Reset is how the user gets an unfiltered
// column, so the seeded tags stay off and every threshold goes to its widest
// value. Sort and group are absent on purpose — the button resets filters.
const SHOW_ALL_BASE: Partial<BoardFilters> = {
    eventsInclude: [],
    eventsExclude: [],
    tagsInclude: [],
    tagsExclude: [],
    tagMatch: 'any',
};

// Widest value per gated field, keyed by wire name so it can be filtered by
// what the column declares. Windows, unit, scope and aggregation are absent on
// purpose: they define what the number means rather than which events survive,
// so resetting them would change the column's subject, not widen it.
const WIDEST_BY_WIRE: Record<string, unknown> = {
    min_capital: 0,
    min_traders: 1,
    max_age_days: MAX_AGE_DAYS_MAX,
    min_drift: 0,
    min_jump: 0,
    min_volume_usd: 0,
    direction: 'any',
    resolved_mode: 'include',
};

// `declared` is the column's filter_spec key list. A column cannot be reset to
// a value it is not allowed to send.
export function showAllFilters(declared: readonly string[]): Partial<BoardFilters> {
    const out: Record<string, unknown> = { ...SHOW_ALL_BASE };
    for (const field of GATED_FIELDS) {
        const wire = FILTER_WIRE_KEYS[field];
        if (!declared.includes(wire)) continue;
        if (!(wire in WIDEST_BY_WIRE)) continue;
        out[field] = WIDEST_BY_WIRE[wire];
    }
    return out as Partial<BoardFilters>;
}

const FILTER_LABELS: Record<string, string> = Object.fromEntries(
    FIELD_CODECS.map(c => [c.field, c.label]),
);

// '' is a real value: it means "no filter", which is different from the
// parameter being absent ("the receiver's own setting stands"). An absent
// parameter has to read back as undefined, or every column looks shared and
// the save dialog opens on a plain page load.
function readList(raw: string | null): string[] | null | undefined {
    if (raw === null) return undefined;
    if (raw === '') return [];
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function readNumber(raw: string | null): number | null | undefined {
    if (raw === null) return undefined;
    if (raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

function readField(codec: FieldCodec, raw: string | null): unknown {
    switch (codec.kind) {
        case 'list':
            return readList(raw);
        case 'number':
            return readNumber(raw);
        case 'enum':
            return raw !== null && codec.values?.includes(raw) ? raw : undefined;
        case 'nullableText':
            return raw === null ? undefined : raw === '' ? null : raw;
        default:
            return raw === null ? undefined : raw;
    }
}

function writeField(codec: FieldCodec, value: unknown): string | undefined {
    if (value === undefined) return undefined;
    switch (codec.kind) {
        case 'list':
            return (value as string[] | null)?.join(',') ?? '';
        case 'number':
            return value === null ? '' : String(value);
        case 'nullableText':
            return value === null ? '' : String(value);
        default:
            return value === null ? '' : String(value);
    }
}

function parseColumn(params: URLSearchParams, prefix: string): Partial<BoardFilters> {
    const out: Record<string, unknown> = {};
    for (const codec of FIELD_CODECS) {
        const parsed = readField(codec, params.get(`${prefix}.${codec.param}`));
        if (parsed !== undefined) out[codec.field] = parsed;
    }
    return out as Partial<BoardFilters>;
}

export type SharedColumns = Partial<Record<ColumnId, Partial<BoardFilters>>>;

export function readSharedColumns(search: string): SharedColumns {
    const params = new URLSearchParams(search);
    const out: SharedColumns = {};
    for (const [id, prefix] of Object.entries(COLUMN_PARAM_PREFIX)) {
        const parsed = parseColumn(params, prefix);
        if (Object.keys(parsed).length > 0) out[id as ColumnId] = parsed;
    }
    return out;
}

export function hasSharedColumns(shared: SharedColumns): boolean {
    return Object.keys(shared).length > 0;
}

const SOURCE_PARAM = 'src';
const KNOWN_SOURCES = new Set(Object.keys(COLUMN_PARAM_PREFIX));

// The pane a link's filters were built against. Absent on every link built
// before categories shipped, which means "apply them to whatever this pane is".
export function readSharedSources(search: string): Partial<Record<ColumnId, ColumnId>> {
    const params = new URLSearchParams(search);
    const out: Partial<Record<ColumnId, ColumnId>> = {};
    for (const [id, prefix] of Object.entries(COLUMN_PARAM_PREFIX)) {
        const raw = params.get(`${prefix}.${SOURCE_PARAM}`);
        if (raw && KNOWN_SOURCES.has(raw)) out[id as ColumnId] = raw as ColumnId;
    }
    return out;
}

// Every field the column has is written, not just the ones that differ from a
// default: the receiver has their own saved settings, so an absent field would
// fall back to theirs and show a different board than the sender's.
export function buildColumnShareUrl(column: ColumnId, f: BoardFilters, source: ColumnId): string {
    const prefix = COLUMN_PARAM_PREFIX[column];
    const params = new URLSearchParams();
    params.set(`${prefix}.${SOURCE_PARAM}`, source);

    for (const codec of FIELD_CODECS) {
        const value = f[codec.field];
        if (codec.optional && value === undefined) continue;
        const written = writeField(codec, value);
        if (written !== undefined) params.set(`${prefix}.${codec.param}`, written);
    }

    const { origin, pathname } = window.location;
    return `${origin}${pathname}?${params.toString()}`;
}

export function clearBoardParams(): void {
    const params = new URLSearchParams(window.location.search);
    for (const prefix of Object.values(COLUMN_PARAM_PREFIX)) {
        for (const key of [...params.keys()]) {
            if (key.startsWith(`${prefix}.`)) params.delete(key);
        }
    }
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState(null, '', next);
}

function describeValue(key: string, value: unknown): string {
    if (Array.isArray(value)) return value.length === 0 ? 'none' : value.join(', ');
    if (value === null) return key === 'maxAgeDays' ? 'no limit' : 'none';
    return String(value);
}

export function describeFilters(partial: Partial<BoardFilters>): string[] {
    return Object.entries(partial)
        .filter(([key]) => FILTER_LABELS[key])
        .map(([key, value]) => `${FILTER_LABELS[key]}: ${describeValue(key, value)}`);
}